import { connect } from 'react-redux';
import { useState, useEffect, useRef } from 'react';

import { login, setLoading, setAlert, siteboardAction } from '../actions/defaultActions';
import {
  Email, SiteboardApiValidator, UserName, GET_SUBSCRIPTION_INVITE_LINK_DETAILS, STOP_USING_INVITE_LINK, INVITE_LINK_EXPIRE_TIME_DAYS, REQUEST_FRESH_SUBSCRIPTION_INVITE_LINK,
  REDEEM_SUBSCRIPTION_INVITE_LINK,
} from '../actions/types';
import { getServerApiResponse } from '../Util';

import PoweredByBar from './PoweredByBar';
import ParallaxSection from './ParallaxSection';
import Tooltip from './Tooltip';
import PushButton from './PushButton';

import Logo from '../SiteBoard_Logo';
import BackIcon from '../icons/backIcon';
import InfoIcon from '../icons/infoIcon';

const testMode = false;

const InviteSignupPage = ({_inviteLinkCode, _inviteLinkEmail, setAlert, login, setLoading, siteboardAction}) => {
  const inputStyle = 'w-full text-white rounded-md px-3 py-1.5 bg-[#444247] hover:bg-[#656565] focus:bg-[#656565]';
  const disabledInputStyle = 'w-full text-white rounded-md px-3 py-1.5 bg-[#444247] focus:bg-[#656565]';

  const [inputFirstName, setInputFirstName] = useState('');
  const [inputLastName, setInputLastName] = useState('');
  const [inputEmail, setInputEmail] = useState(_inviteLinkEmail);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviterName, setInviterName] = useState(true);
  const [inviterEmail, setInviterEmail] = useState(true);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [projectNames, setProjectNames] = useState([]);
  const [isAccountManager, setIsAccountManager] = useState([]);
  const [isExpired, setIsExpired] = useState(false);
  const [newInviteSent, setNewInviteSent] = useState(false);

  const fetchInviteState = useRef('none');

  useEffect(() => {
    if(testMode) {
      setInputEmail('myemail@stuff.com');
      setIsExpired(false);
      setInviterName('Strongly Rickmanson');
      setInviterEmail('ricky.strong@construction.com');
      setIsExistingUser(false);
      setProjectNames(['one', 'two']);
      setIsAccountManager(true);
      setInviteLoading(false);
      return;
    }

    if(fetchInviteState.current !== 'none') return;

    const onApiSuccess = (data) => {
      try {
        fetchInviteState.current = 'complete';
        const validOutput = SiteboardApiValidator[GET_SUBSCRIPTION_INVITE_LINK_DETAILS].validateOutput(data); 
        setIsExpired(validOutput.isExpired);
        setInviterName(validOutput.inviterName);
        setInviterEmail(validOutput.inviterEmail);
        setIsExistingUser(validOutput.isExistingUser);
        setProjectNames(validOutput.projectNames);
        setIsAccountManager(validOutput.isAccountManager);
        setInviteLoading(false);
      } catch(error) {
        onApiError(error.message);
      }
    };
    const onApiError = (errorMessage) => {
      fetchInviteState.current = 'none';
      siteboardAction(STOP_USING_INVITE_LINK, {});
      window.history.pushState('', '', '?'); // clear the url
      setAlert(typeof errorMessage === 'string' ? errorMessage : 'error getting invite info');
    };
    getServerApiResponse({ 
      action: GET_SUBSCRIPTION_INVITE_LINK_DETAILS,
      inviteEmail: _inviteLinkEmail,
      inviteCode: _inviteLinkCode,
    }, onApiSuccess, onApiError);

    fetchInviteState.current = 'pending';
  }, [_inviteLinkCode, _inviteLinkEmail, setAlert, siteboardAction]);

  const acceptInvite = () => {
    const onApiSuccess = (data) => {
      setLoading('');
      try {
        const validOutput = SiteboardApiValidator[REDEEM_SUBSCRIPTION_INVITE_LINK].validateOutput(data); 
        login(validOutput.user, validOutput.sessionKey); 
        siteboardAction(STOP_USING_INVITE_LINK, {});
        window.history.pushState('', '', '?'); // clear the url
      } catch(error) {
        onApiError(error.message);
      }
    };
    const onApiError = (errorMessage) => {
      setLoading('');
      setAlert(typeof errorMessage === 'string' ? errorMessage : 'There was an error accepting your invite.');
    };
    setLoading('Accepting Invite & Logging You In');
    getServerApiResponse({
      action: REDEEM_SUBSCRIPTION_INVITE_LINK,
      firstName: inputFirstName,
      lastName: inputLastName,
      inviteEmail: _inviteLinkEmail,
      inviteCode: _inviteLinkCode,
    }, onApiSuccess, onApiError);
  };

  const requestNewInvite = () => {
    const onApiSuccess = (data) => {
      setLoading('');
      setNewInviteSent(true);
    };
    const onApiError = (errorMessage) => {
      setLoading('');
      setAlert(typeof errorMessage === 'string' ? errorMessage : 'error getting invite info');
    };
    setLoading('Sending Invite Email');
    getServerApiResponse({ 
      action: REQUEST_FRESH_SUBSCRIPTION_INVITE_LINK,
      inviteEmail: _inviteLinkEmail,
      inviteCode: _inviteLinkCode,
    }, onApiSuccess, onApiError);
  };
  
  const getProjectManagerJSX = () => {
    return (
      <Tooltip 
        align='left'
        tooltipContent='Account Managers have the authority to create, edit, and assign members to all projects within an account.'
      >
        <div className='flex gap-1 items-center underline decoration-dotted underline-offset-4 cursor-help'>Account Manager</div>
      </Tooltip>
    );
  };

  return (
    <div className={`relative flex flex-col justify-between min-h-screen bg-[#151515] text-white overflow-x-auto`}>
      <div className='relative z-[1] grow flex flex-col justify-center items-center max-w-screen-lg px-2 py-6 text-[1.2em] w-full mx-auto'>
        <div className='grow flex flex-col justify-center items-center max-w-[500px]'>
          <div className='flex flex-col gap-6'>
            {/* SiteBoard logo */}
            <div>
              <a href='/' className={'flex items-center gap-x-2 font-bold text-[1.8em] px-2 sm:px-6'}>
                <div className={`${inviteLoading ? 'animate-[spin_1s_linear_infinite]' : ''}`}>
                  <Logo size={'1em'} />
                </div>
                <div className='flex flex-col'>
                {process.env.REACT_APP_WEBSITE_NAME} 
                </div>
              </a>
              {inviteLoading === true && 
                <div className='font-medium text-[#aaa] text-[1.2em]'>Retrieving Your Invitation</div>
              }
            </div>
            {(inviteLoading === false && !isExpired) &&
              <div className='flex flex-col gap-2 max-w-[90vw] px-2 sm:px-6'>
                <div className='font-bold text-[1.2em] text-[#f9a928]'>
                  <span className='text-nowrap'>{inviterName}</span> (<span className='text-white'>{inviterEmail}</span>) has invited you to join their account.
                </div>
                <div className=''>
                  {(isAccountManager && projectNames.length > 0) && <div>You've been added as an {getProjectManagerJSX()} and assigned to the following project(s):</div>}
                  {(isAccountManager && projectNames.length === 0) && <div>You've been added as an {getProjectManagerJSX()}.</div>}
                  {(!isAccountManager && projectNames.length > 0) && <div>You've been assigned to the following project(s):</div>}
                </div>
                <div className=''>
                  {projectNames.length > 0 &&
                    <div></div>
                  }
                  {projectNames.map((projectName, index) => (
                    <div key={index} className='flex items-center gap-2 w-full text-left px-2' >
                      <span className='w-2 min-w-2 h-2 min-h-2 rounded-full bg-[#fff]'></span>
                      <span className=''>{projectName}</span>
                    </div>
                  ))}
                </div>
              </div>
            }
            {(inviteLoading === false && !isExistingUser && !isExpired) && 
              <div className='flex flex-col gap-4 bg-[#44444466] rounded-lg p-4'>
                <div className='font-medium'>To join them, create a free account.</div>
                <div className='flex flex-col gap-y-6'>
                  {/* First and Last name */}
                  <div className='flex gap-x-2'>
                    <div>
                      <label className='block text-sm font-medium mb-1'>First Name</label>
                      <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                        <input 
                          value={inputFirstName}
                          className={inputStyle}
                          type='text' 
                          onChange={(event) => { setInputFirstName(event.target.value); }}
                        />
                      </div>
                    </div>
                    <div>
                      <label className='block text-sm font-medium mb-1'>Last Name</label>
                      <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                        <input 
                          value={inputLastName}
                          className={inputStyle}
                          type='text' 
                          onChange={(event) => { setInputLastName(event.target.value); }}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Email Input */}
                  <div>
                    <label className='block text-sm font-medium mb-1'>Email</label>
                    <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                      <input 
                        value={inputEmail}
                        className={disabledInputStyle}
                        type='text' 
                        disabled={true}
                        onChange={(event) => {}}
                      />
                    </div>
                  </div>
                  {/* submit email button */}
                  <PushButton 
                    textColor='black'
                    bgColor='#f9a928'
                    hoverBgColor='#f79800'
                    onClick={() => { acceptInvite(); }}
                  >
                    Sign up & Accept Invite
                  </PushButton>
                </div>
              </div>
            }
            {(inviteLoading === false && isExistingUser && !isExpired) && 
              <div className='flex flex-col gap-4 bg-[#44444444] rounded-lg p-4'>
                <div className='font-medium'>Click the button below to accept their invite.</div>
                <div className='flex flex-col gap-y-6'>
                  {/* Email Input */}
                  <div>
                    <label className='block text-sm font-medium mb-1'>Your Email</label>
                    <div className='rounded-[12px] border border-[#0000003f] h-[32px] '>
                      <input 
                        value={inputEmail}
                        className={disabledInputStyle}
                        type='text' 
                        disabled={true}
                        onChange={(event) => {}}
                      />
                    </div>
                  </div>
                  {/* submit email button */}
                  <PushButton 
                    textColor='black'
                    bgColor='#f9a928'
                    hoverBgColor='#f79800'
                    onClick={() => { acceptInvite(); }}
                  >
                    Accept Invite
                  </PushButton>
                </div>
              </div>
            }
            {(inviteLoading === false && isExpired && !newInviteSent) && 
              <>
                <div>Your invite link is more than {INVITE_LINK_EXPIRE_TIME_DAYS} days old and has expired for security reasons. To proceed, we can send a new invitation to <b>{_inviteLinkEmail}</b>.</div>
                <div className='flex flex-col gap-4 bg-[#44444444] rounded-lg p-4'>
                  <div className='font-medium'>Please click the button below to receive a fresh invite link via email.</div>
                  <div className='flex flex-col gap-y-6'>
                    {/* submit email button */}
                    <PushButton 
                      textColor='black'
                      bgColor='#f9a928'
                      hoverBgColor='#f79800'
                      onClick={() => { requestNewInvite(); }}
                    >
                      Send New Invite Link
                    </PushButton>
                  </div>
                </div>
              </>
            }
            {(inviteLoading === false && isExpired && newInviteSent) && 
              <div>We sent a new invite link to <b>{_inviteLinkEmail}</b>. Please check your inbox and click the new link to accept your invitation. If you don’t see the email shortly, be sure to check your spam or junk folder.</div>
            }
            {(inviteLoading === false) &&
              <>
                <div className='italic px-6'>
                  {process.env.REACT_APP_WEBSITE_NAME} lets you quickly create and update <span className='break-keep'>3-week</span> lookahead schedules without spreadsheets.
                </div>
                <div className='italic px-6'>
                  If you need assistance contact us at {process.env.REACT_APP_SUPPORT_EMAIL}
                </div>
              </>
            }
          </div>
        </div>
      </div>
      <div className='z-[1]'>
        <PoweredByBar />
      </div>
      {/* construction site background */}
      <div className='z-[0] absolute top-[0vh] left-[0vw] opacity-[1] pointer-events-none'>
        <ParallaxSection parallaxMultiplier={0.5}>
          {/*<img width='906' height='1000' className='h-[120vh] object-cover object-top' src='/images/constructionSite2.svg' alt='line drawing of a construction site' />*/}
          <img width='1536' height='1024' className='w-[100vw] min-h-[100vh] object-cover object-top' src='/images/constructionSite3.jpg' alt='a construction site' />
          {/* gradient at bottom to fade image into gray background */}
          <div className='absolute bottom-0 left-0 h-[15vh] w-[100vw] bg-gradient-to-t from-[#151515] to-[#15151500]'></div>
        </ParallaxSection>
      </div>
      {/* top right floating logo */}
      <div className='fixed top-[4em] left-[100%] translate-x-[-90%] opacity-[0.10] pointer-events-none'>
        <div className='sm:hidden'><Logo size={250} /></div>
        <div className='hidden sm:block 2xl:hidden'><Logo size={300} /></div>
        <div className='hidden 2xl:block'><Logo size={400} /></div>
      </div>
    </div>
  );
}

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _inviteLinkCode: state.localEphemeral.inviteLinkCode,
  _inviteLinkEmail: state.localEphemeral.inviteLinkEmail,
}};
export default connect(mapStateToProps, {setAlert, login, setLoading, siteboardAction})(InviteSignupPage);
